import React from 'react';
import SceneList from './SceneList';
import { styled } from 'baseui';
import { useSelector } from 'react-redux';
import { selectDisableEditorState } from '@/store/slices/editor/selector';
import { Z_INDEX } from '@/constants/z-index';
import {
  ScenesProvider,
  type ScenesOrientation,
  useScenesContext,
  BASE_CONTAINER_WIDTH,
  HOVERING_CONTAINER_WIDTH,
  WRAP_SCENE_LIST_PADDING_WHEN_HOVERING,
} from './ScenesProvider';
import { ScenesPanelShell } from './ScenesPanelShell';
import { ScenesExternalDataProps, SceneItemConfig } from './sceneItemConfig';
import { LayoutUtils } from '@/utils/LayoutUtils';

// Re-export constants for backward compatibility
export { BASE_CONTAINER_WIDTH, HOVERING_CONTAINER_WIDTH } from './ScenesProvider';

const WrapSceneList = styled(ScenesPanelShell, () => ({
  position: 'absolute',
  right: '0px',
  top: '50%',
  transform: 'translateY(-50%)',
  height: '100%',
  width: `${BASE_CONTAINER_WIDTH}px`,
  opacity: 1,
  zIndex: 3,
  transition: 'width 0.3s ease-out, padding 0.3s ease-out',
  willChange: 'width, padding',
  ':hover': {
    width: `${HOVERING_CONTAINER_WIDTH}px !important`,
    padding: WRAP_SCENE_LIST_PADDING_WHEN_HOVERING,
    transition: 'width 0.3s ease-out, padding 0.3s ease-out',
  },
}))

const SidePanelShell = styled(ScenesPanelShell, () => ({
  width: `${LayoutUtils.PAGES_WIDTH}px`,
  height: '100%',
}))

// Pure UI shell — all logic lives in ScenesProvider/useScenesContext
const ScenesShell: React.FC = () => {
  const {
    hide,
    disable,
    wrapSceneListRef,
    isContainerHovering,
    setIsContainerHovering,
    openEditMenuId,
    openDownloadPopoverSceneId,
    isDragging,
  } = useScenesContext()

  return (
    <div style={{ zIndex: Z_INDEX.SCENES, visibility: hide ? 'hidden' : 'visible', opacity: disable ? 0.5 : 1, pointerEvents: disable ? 'none' : 'all' }}>
      <WrapSceneList
        id="wrap-scene-list"
        ref={wrapSceneListRef}
        style={{ pointerEvents: disable ? 'none' : 'all', zIndex: 1 }}
        onMouseEnter={() => setIsContainerHovering(true)}
        onMouseLeave={() => {
          if (openEditMenuId || openDownloadPopoverSceneId || isDragging) return
          wrapSceneListRef.current.style.width = `${BASE_CONTAINER_WIDTH}px`
          setIsContainerHovering(false)
        }}
      >
        <SceneList />
      </WrapSceneList>
    </div>
  )
}

interface ScenesProps {
  hide?: boolean
  disable?: boolean
  /** When 'sidePanel', renders a fixed-width panel (e.g. AI Studio results). Skips editor-mode guards. */
  panelVariant?: 'editor' | 'sidePanel'
  /** sidePanel only: 'horizontal' lays the items out in a row that fills the parent. Defaults to 'vertical'. */
  orientation?: ScenesOrientation
  /** Externally controlled items, selection, config and callbacks. Required when panelVariant='sidePanel'. */
  externalData?: ScenesExternalDataProps
  /** Editor-mode config override. */
  config?: SceneItemConfig
  /** Override the download destination label. */
  getDownloadDestination: () => string
  /** Override the download source label. */
  getDownloadSource: () => string
}

const Scenes: React.FC<ScenesProps> = ({ hide = false, disable = false, panelVariant, orientation = 'vertical', externalData, config, getDownloadDestination, getDownloadSource }) => {
  const disableEditorState = useSelector(selectDisableEditorState)

  if (panelVariant === 'sidePanel') {
    return (
      <ScenesProvider externalData={externalData} config={config} getDownloadDestination={getDownloadDestination} getDownloadSource={getDownloadSource} orientation={orientation}>
        {orientation === 'horizontal' ? (
          // Unwrapped: an extending styled() would not pass $horizontal on. A row fills its parent.
          <ScenesPanelShell $horizontal $style={{ width: '100%', height: '100%' }}>
            <SceneList />
          </ScenesPanelShell>
        ) : (
          <SidePanelShell>
            <SceneList />
          </SidePanelShell>
        )}
      </ScenesProvider>
    )
  }

  // Editor variant (default)
  if (disableEditorState.scenes === 'hidden') return null

  return (
    <ScenesProvider hide={hide} disable={disable} config={config} getDownloadDestination={getDownloadDestination} getDownloadSource={getDownloadSource}>
      <ScenesShell />
    </ScenesProvider>
  )
}

export default Scenes;
